import apiClient from './api.service';
import {
  CustomField,
  CreateCustomFieldData,
  UpdateCustomFieldData,
  OrderCustomFieldValue,
  UpdateOrderCustomFieldsData,
  FieldTypeDefinition
} from '../types/customField.types';
import { isValidPhoneValue } from '../utils/phoneRole.utils';

/**
 * Custom Field Service
 * Handles all API calls related to custom fields
 */

const STORE_FIELDS_TTL_MS = 45_000;
const ORDER_FIELD_VALUES_TTL_MS = 20_000;

class CustomFieldService {
  private storeFieldsCache = new Map<string, { data: CustomField[]; expiresAt: number }>();
  private storeFieldsInflight = new Map<string, Promise<CustomField[]>>();
  private orderValuesCache = new Map<string, { data: OrderCustomFieldValue[]; expiresAt: number }>();
  private orderValuesInflight = new Map<string, Promise<OrderCustomFieldValue[]>>();

  private storeFieldsKey(storeId: number, filters?: { is_active?: boolean; type?: string }) {
    return `${storeId}:${filters?.is_active ?? ''}:${filters?.type ?? ''}`;
  }

  private forgetStoreFields(storeId: number) {
    const prefix = `${storeId}:`;
    Array.from(this.storeFieldsCache.keys()).forEach((key) => {
      if (key.startsWith(prefix)) this.storeFieldsCache.delete(key);
    });
  }

  private forgetOrderValues(storeId: number, orderId: number) {
    this.orderValuesCache.delete(`${storeId}:${orderId}`);
  }

  /**
   * Get all custom fields for a store
   */
  async getStoreFields(storeId: number, filters?: { is_active?: boolean; type?: string }): Promise<CustomField[]> {
    const key = this.storeFieldsKey(storeId, filters);
    const cached = this.storeFieldsCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.data;
    const existing = this.storeFieldsInflight.get(key);
    if (existing) return existing;

    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) {
      params.append('is_active', filters.is_active.toString());
    }
    if (filters?.type) {
      params.append('type', filters.type);
    }

    const request = apiClient.get(`/stores/${storeId}/custom-fields?${params.toString()}`)
      .then((response) => {
        const data = response.data.data as CustomField[];
        this.storeFieldsCache.set(key, { data, expiresAt: Date.now() + STORE_FIELDS_TTL_MS });
        return data;
      })
      .finally(() => {
        this.storeFieldsInflight.delete(key);
      });
    this.storeFieldsInflight.set(key, request);
    return request;
  }

  /**
   * Get a specific custom field
   */
  async getFieldById(storeId: number, fieldId: number): Promise<CustomField> {
    const response = await apiClient.get(`/stores/${storeId}/custom-fields/${fieldId}`);
    return response.data.data;
  }

  /**
   * Create a new custom field
   */
  async createField(storeId: number, data: CreateCustomFieldData): Promise<CustomField> {
    const response = await apiClient.post(`/stores/${storeId}/custom-fields`, data);
    this.forgetStoreFields(storeId);
    return response.data.data;
  }

  /**
   * Update a custom field
   */
  async updateField(storeId: number, fieldId: number, data: UpdateCustomFieldData): Promise<CustomField> {
    const response = await apiClient.put(`/stores/${storeId}/custom-fields/${fieldId}`, data);
    this.forgetStoreFields(storeId);
    return response.data.data;
  }

  /**
   * Delete a custom field
   */
  async deleteField(storeId: number, fieldId: number): Promise<void> {
    await apiClient.delete(`/stores/${storeId}/custom-fields/${fieldId}`);
    this.forgetStoreFields(storeId);
  }

  /**
   * Toggle field active status
   */
  async toggleField(storeId: number, fieldId: number): Promise<CustomField> {
    const response = await apiClient.patch(`/stores/${storeId}/custom-fields/${fieldId}/toggle`);
    this.forgetStoreFields(storeId);
    return response.data.data;
  }

  /**
   * Reorder fields
   */
  async reorderFields(storeId: number, fieldIds: number[]): Promise<CustomField[]> {
    const response = await apiClient.put(`/stores/${storeId}/custom-fields/reorder`, { fieldIds });
    this.forgetStoreFields(storeId);
    return response.data.data;
  }

  /**
   * Get available field types
   */
  async getFieldTypes(storeId: number): Promise<FieldTypeDefinition[]> {
    const response = await apiClient.get(`/stores/${storeId}/custom-fields/types`);
    return response.data.data;
  }

  /**
   * Get custom field values for an order
   */
  async getOrderFieldValues(
    storeId: number,
    orderId: number,
    options?: { fresh?: boolean }
  ): Promise<OrderCustomFieldValue[]> {
    const key = `${storeId}:${orderId}`;
    if (options?.fresh) this.orderValuesCache.delete(key);
    const cached = this.orderValuesCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.data;
    const existing = this.orderValuesInflight.get(key);
    if (existing) return existing;
    const request = apiClient.get(`/stores/${storeId}/orders/${orderId}/custom-fields`)
      .then((response) => {
        const data = response.data.data as OrderCustomFieldValue[];
        this.orderValuesCache.set(key, { data, expiresAt: Date.now() + ORDER_FIELD_VALUES_TTL_MS });
        return data;
      })
      .finally(() => {
        this.orderValuesInflight.delete(key);
      });
    this.orderValuesInflight.set(key, request);
    return request;
  }

  /**
   * Update custom field values for an order
   */
  async updateOrderFieldValues(
    storeId: number,
    orderId: number,
    data: UpdateOrderCustomFieldsData
  ): Promise<void> {
    await apiClient.put(`/stores/${storeId}/orders/${orderId}/custom-fields`, data);
    this.forgetOrderValues(storeId, orderId);
  }

  /**
   * Validate field value based on type (non-blocking)
   */
  validateFieldValue(type: string, value: string): { isValid: boolean; message?: string } {
    if (!value || value.trim() === '') {
      return { isValid: true }; // Empty is valid (unless required, checked separately)
    }

    switch (type) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value)
          ? { isValid: true }
          : { isValid: false, message: 'Format d\'email invalide' };

      case 'phone':
        return isValidPhoneValue(value)
          ? { isValid: true }
          : { isValid: false, message: 'Format de téléphone invalide (ex: 0612345678)' };

      case 'number':
        const numberRegex = /^-?\d+$/;
        return numberRegex.test(value)
          ? { isValid: true }
          : { isValid: false, message: 'Doit être un nombre entier' };

      case 'float':
        const floatRegex = /^-?\d+(\.\d+)?$/;
        return floatRegex.test(value)
          ? { isValid: true }
          : { isValid: false, message: 'Doit être un nombre décimal' };

      case 'date':
        const date = new Date(value);
        return !isNaN(date.getTime())
          ? { isValid: true }
          : { isValid: false, message: 'Format de date invalide' };

      default:
        return { isValid: true };
    }
  }
}

export default new CustomFieldService();

